// The admin hero's "all of Taekus" pair (Kareem, 2026-10-23).
//
// The four pool cards on AdminOverview read the TK-C pool only (the
// taekus_pool_snapshot row every investor may see). Kareem also wanted two
// numbers that span every Taekus account he holds — the business debit, the
// pool card and the rest, credit lines excluded — so these helpers turn the raw
// /api/banking/internal-accounts/ payload into that total, and the total
// into "what would be mine if every position settled". Pure functions so
// tests/investment-v2/taekus-accounts.test.mjs can exercise them under
// plain node.
//
// Why the browser reads the proxy instead of a snapshot column: the snapshot
// table is readable by every signed-in investor, and the balances of
// Kareem's other accounts are his alone. taekus-read already refuses anyone
// who is not an operator, so the admin hero fetches live and nothing new is
// stored.

// Taekus's public OpenAPI spec (app.taekus.com/developers/schema-public/,
// InternalAccountsSchema) types every internal account PREPAID or CREDIT.
// On a CREDIT account available_balance is the credit line (Kareem
// 2026-10-23: "the 12,500 is the credit line, not money in the account"),
// so those are left out by type. Nothing is excluded by card number: the
// first cut keyed on ·5139 and that threw out a real deposit account.
export function isCreditAccount(a) {
  return String(a?.type || '').toUpperCase() === 'CREDIT';
}

/**
 * Every DEPOSIT account in an internal-accounts payload, summed.
 * Accepts either the bare array Taekus sometimes returns or the
 * `{ internal_accounts: [...] }` wrapper. Credit accounts (type CREDIT)
 * are left out and counted in `excluded`; accounts whose balance is not a
 * number are skipped and counted in `skipped`, so a half-broken payload
 * never silently reads as a smaller total.
 */
export function sumInternalAccounts(payload) {
  const list = Array.isArray(payload) ? payload : (payload?.internal_accounts || []);
  const accounts = [];
  let skipped = 0;
  let excluded = 0;
  for (const a of list) {
    if (isCreditAccount(a)) { excluded++; continue; }
    const available = Number(a?.available_balance);
    if (!Number.isFinite(available)) { skipped++; continue; }
    accounts.push({
      uuid: a.uuid || null,
      name: a.display_name || a.name || null,
      type: a.type || 'PREPAID',
      last4: a.accountLast4 ? String(a.accountLast4).slice(-4) : null,
      available,
    });
  }
  const total = accounts.reduce((s, a) => s + a.available, 0);
  return { total, count: accounts.length, skipped, excluded, accounts };
}

/**
 * What Kareem keeps once everyone settles: every dollar in Taekus, minus
 * what other households own in the pool (they would withdraw it), plus what
 * other households owe (they would pay it back).
 */
export function netAfterSettling({ totalAll, othersOwn = 0, othersOwe = 0 }) {
  return Number(totalAll || 0) - Number(othersOwn || 0) + Number(othersOwe || 0);
}
